/** Typed client of the dashboard API (`/api/v1`), generated types from packages/shared-types. */
import createClient, { type Middleware } from "openapi-fetch";
import type { components, paths } from "@solar/shared-types";
import { authHeaders } from "../auth/auth";
import { config } from "../config";

export type S = components["schemas"];

export class ApiError extends Error {
  status: number;
  problem: Partial<S["Problem"]>;
  constructor(status: number, problem: ApiError["problem"]) {
    super(problem.detail || problem.title || `Request failed (${status})`);
    this.status = status;
    this.problem = problem;
  }
}

const auth: Middleware = {
  async onRequest({ request }) {
    const headers = await authHeaders();
    for (const [k, v] of Object.entries(headers)) request.headers.set(k, v);
    return request;
  },
};

export const api = createClient<paths>({ baseUrl: `${config.apiUrl}/api` });
api.use(auth);

/** Unwraps an openapi-fetch result: the data, or an ApiError. */
export async function unwrap<T>(promise: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await promise;
  if (!response.ok) {
    const problem = (error && typeof error === "object" ? error : { title: response.statusText }) as ApiError["problem"];
    throw new ApiError(response.status, problem);
  }
  return data as T;
}

/** A raw authenticated request (images, multipart uploads). */
export async function rawFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  for (const [k, v] of Object.entries(await authHeaders())) headers.set(k, v);
  const response = await fetch(`${config.apiUrl}${path}`, { ...init, headers });
  if (!response.ok) {
    let problem: ApiError["problem"] = { title: response.statusText };
    try {
      problem = await response.json();
    } catch {
      /* not JSON */
    }
    throw new ApiError(response.status, problem);
  }
  return response;
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const fields = error.problem.errors?.map((e) => `${(e.loc || []).join(".")}: ${e.msg}`);
    return fields?.length ? `${error.message} — ${fields.join("; ")}` : error.message;
  }
  if (error instanceof Error) return error.message;
  return String(error);
}
